// src/features/services/ttlock/components/table_admin_rooms.tsx
import React from 'react';
import { getCookie } from '@/lib/http/bffClient';
import { useMutation } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import type { Room } from './table_superadmin_rooms';

type Props = {
  rooms: Room[];
  loading?: boolean;
  error?: unknown;
  onRefresh?: () => void;
};

/* cookie helper imported above */

export default function Table_admin_rooms({ rooms, loading = false, error, onRefresh }: Props) {
  const toggleActive = useMutation({
    mutationFn: async ({ id_scope, next }: { id_scope: number; next: boolean }) => {
      const csrf = getCookie('csrf');
      const res = await fetch(`/bff/ttlock/rooms/${id_scope}/active`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          ...(csrf ? { 'X-CSRF': csrf } : {}),
        },
        body: JSON.stringify({ active: next }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `POST /bff/ttlock/rooms/${id_scope}/active → ${res.status}`);
      }
      return true;
    },
    onSuccess: () => { onRefresh?.(); },
  });

  const columns = React.useMemo<ColumnDef<Room>[]>(() => ([
    {
      header: <span className="text-neutral-300">Name</span>,
      accessor: 'room_name',
      Cell: (v: string | null) => <span className="text-neutral-300">{v ?? '—'}</span>,
    },
    {
      header: <span className="text-neutral-300">Active</span>,
      accessor: 'is_active',
      Cell: (v: boolean | undefined) => (
        <span className={['text-sm', v ? 'text-emerald-400' : 'text-neutral-400'].join(' ')}>
          {v ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: <span className="text-neutral-300">Actions</span>,
      accessor: 'id_scope',
      Cell: (_v, row) => {
        const next = !row.is_active;
        const isBusy = toggleActive.isPending && toggleActive.variables?.id_scope === row.id_scope;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!row.id_scope) return;
              toggleActive.mutate({ id_scope: row.id_scope, next });
            }}
            disabled={isBusy || !row.id_scope}
            className={[
              'text-sm font-medium transition-colors underline-offset-2 hover:underline',
              isBusy
                ? 'opacity-60 cursor-not-allowed text-neutral-400'
                : next
                  ? 'text-emerald-400 hover:text-emerald-300'
                  : 'text-rose-400 hover:text-rose-300',
            ].join(' ')}
            title={next ? 'Activate room' : 'Deactivate room'}
          >
            {isBusy ? 'Working…' : next ? 'Activate' : 'Deactivate'}
          </button>
        );
      },
    },
  ]), [toggleActive.isPending, toggleActive.variables]);

  if (loading) return <p className="text-neutral-400">Loading rooms…</p>;
  if (error)   return <p className="text-rose-400">Failed to load rooms.</p>;

  return (
    <MinimalisticTable<Room>
      columns={columns}
      data={rooms}
      noDataMessage="No rooms to show yet."
      showSearch
      searchPlaceholder="Filter rooms…"
      isRowClickable={() => false}
    />
  );
}
